import type { Dictionary } from "./tw";

const en: Dictionary = {
  metadata: {
    titleDefault: "QuickDash - Data Integration Tool",
    titleTemplate: "%s | QuickDash",
    description:
      "QuickDash provides a one-stop data integration solution, enabling your team to quickly connect, analyze, and visualize data from various sources.",
    keywords: [
      "Data Integration",
      "Data Studio",
      "Data Analytics",
      "Advertising Analytics",
    ],
    authorName: "QuickDash",
    ogTitle: "QuickDash | Data Connector Tool",
    ogDescription:
      "Connect common data sources like META Ads, GA4, GADS. Build data reports with one click, boosting efficiency by 200%.",
    ogSiteName: "QuickDash",
  },
  navbar: {
    logoText: "QuickDash",
    plans: "Plans",
    templates: "Templates",
    tutorials: "Tutorials",
    connectors: "Connectors",
    connectorMetaPage: "Meta Page",
    about: "About Us",
    articles: "Articles",
    signUp: "Sign Up",
  },
  footer: {
    sectionProduct: "Products",
    linkPlans: "Plans",
    linkTutorial: "Tutorials",
    sectionCompany: "About",
    linkAbout: "About Us",
    linkArticles: "Articles",
    linkContact: "Contact Us",
    sectionSupport: "Support",
    linkRefund: "Refund Policy",
    linkTerms: "Terms of Service",
    linkPrivacy: "Privacy Policy",
    logoText: "QuickDash",
    tagline: "One-stop data integration solution.",
    facebookPage: "Facebook Page",
    taxId: "Tax ID: 96752719",
    copyright: "QuickDash. All rights reserved.",
  },
  hero: {
    headline: "Omni-Media",
    headlineAccent: "Reporting",
    subHeadlines: [
      "Omnichannel Data Integration",
      800,
      "AI MCP Data Insights",
      800,
      "3-Min Data-Driven Action",
      800,
    ],
    ctaSignUp: "Start Free Trial",
    platformsLabel: "Supported platforms",
    tabAll: "All",
    dashOverview: "Overview",
    dashRange: "This month",
    kpiImpressions: "Impressions",
    kpiClicks: "Clicks",
    kpiConversions: "Conversions",
    kpiConvValue: "Conv. Value",
    trendTitle: "Trend Comparison",
    seriesImpressions: "Impressions",
    seriesClicks: "Clicks",
    pieTitle: "Media Share",
    tableTitle: "Media Performance",
    tableCols: ["Media", "Impr.", "Clicks", "Conv.", "CVR", "Conv. Value"],
    chip: "more+",
    otherLabel: "Other",
  },
  pricing: {
    freeTrial: "Free Trial",
    days: "Days",
    mostPopular: "Most Popular",
    aiMcpBadgeLabel: "MCP",
    perYear: "x 12 months",
    perMonth: "/ mo",
    billingMonthly: "Monthly",
    billingYearly: "Yearly",
    yearlyDiscountBadge: "Save 10%",
    needHelp: "Need Assistance?",
    contactLine: "LINE Support",
    contactMessenger: "FB Support",
  },
  pricingPlans: [
    {
      currency: "TWD$",
      name: "Basic Plan",
      priceYearly: 680,
      description: "Great for most brands and solo marketers",
      isRecommended: false,
      ctaText: "Start Free Trial",
      features: [
        { feature: "10 linked accounts", included: true },
        { feature: "Excel integration", included: true },
        { feature: "Threads Crawler", included: true },
        { feature: "Messenger API", included: true },
        { feature: "KOL Search API", included: true },
        { feature: "Hidden Facebook audiences", included: true },
        { feature: "Automated report email", included: true },
        { feature: "Google Sheets integration", included: true },
        { feature: "Google Sheets schedule x5", included: true },
        { feature: "Data Studio integration", included: true },
        { feature: "Data Studio templates", included: true },
        { feature: "GA4 Audit Tool", included: true },
        { feature: "GTM Setup Tool", included: false },
        { feature: "Google Ads integration", included: false },
        { feature: "Microsoft Ads integration", included: false },
        { feature: "CYBERBIZ integration", included: false },
        { feature: "1SHOP integration", included: false },
        { feature: "Exclusive dashboard", included: false },
        { feature: "Extra users", included: false },
        { feature: "High-volume mode", included: false },
      ],
    },
    {
      currency: "TWD$",
      name: "Advanced Plan",
      priceYearly: 1390,
      description: "For brands and small agencies scaling data operations",
      isRecommended: false,
      showAiMcpBadge: true,
      ctaText: "Get Started",
      features: [
        { feature: "25 linked accounts", included: true },
        { feature: "Excel integration", included: true },
        { feature: "Threads Crawler", included: true },
        { feature: "Messenger API", included: true },
        { feature: "KOL Search API", included: true },
        { feature: "Hidden Facebook audiences", included: true },
        { feature: "Automated report email", included: true },
        { feature: "Google Sheets integration", included: true },
        { feature: "Google Sheets schedule x15", included: true },
        { feature: "Data Studio integration", included: true },
        { feature: "Data Studio templates", included: true },
        { feature: "GA4 Audit Tool", included: true },
        { feature: "GTM Setup Tool", included: true },
        { feature: "Google Ads integration", included: true },
        { feature: "Exclusive dashboard", included: true },
        { feature: "Extra users", included: true },
        { feature: "MCP Report Reading", included: true },
        { feature: "MCP Ad Operations", included: false },
        { feature: "Microsoft Ads integration", included: false },
        { feature: "CYBERBIZ integration", included: false },
        { feature: "1SHOP integration", included: false },
        { feature: "High-volume mode", included: false },
      ],
    },
    {
      currency: "TWD$",
      name: "Premium Plan",
      priceYearly: 3940,
      description: "Best for large agencies and enterprise teams",
      isRecommended: true,
      showAiMcpBadge: true,
      ctaText: "Start Free Trial",
      features: [
        { feature: "100 linked accounts", included: true },
        { feature: "Excel integration", included: true },
        { feature: "Threads Crawler", included: true },
        { feature: "Messenger API", included: true },
        { feature: "KOL Search API", included: true },
        { feature: "Hidden Facebook audiences", included: true },
        { feature: "Automated report email", included: true },
        { feature: "Google Sheets integration", included: true },
        { feature: "Google Sheets schedule x50", included: true },
        { feature: "Data Studio integration", included: true },
        { feature: "Data Studio templates", included: true },
        { feature: "GA4 Audit Tool", included: true },
        { feature: "GTM Setup Tool", included: true },
        { feature: "Google Ads integration", included: true },
        { feature: "CYBERBIZ integration", included: true },
        { feature: "1SHOP integration", included: true },
        { feature: "Exclusive dashboard", included: true },
        { feature: "MCP Report Reading", included: true },
        { feature: "MCP Ad Operations", included: true },
        { feature: "Extra users", included: true },
        { feature: "Microsoft Ads integration", included: true },
        { feature: "High-volume mode", included: true },
      ],
    },
  ],
  process: {
    heading: "Save Monthly",
    headingUnit: "Hours",
    unitSingular: "Hour",
    step1Title: "Set Up Data Sources",
    step2Title: "Customize Fields",
    step3Title: "Report Design",
    step4Title: "Monthly Recurring Reports",
  },
  featureCards: {
    heading: "Data Integration",
    headingUnit: "Minutes",
    metaTitle: "Meta Products",
    metaDesc: "Meta Ads/Pages, IG/Threads Social",
    googleTitle: "Google Products",
    lineTitle: "LINE LAP",
    ecommerceTitle: "E-Commerce Platforms",
    ecommerceDesc: "CYBERBIZ, SHOPIFY, 1SHOP, Ruten, and other platforms",
  },
  photoCarousel: {
    heading: "Users",
    dimensions_total: "Total Users",
    dimensions_brand: "Brands",
    dimensions_ads: "Agencies",
    graphTitle: "Users",
  },
  featureShowcase: {
    heading: "All in One",
    subheading: "Swipe to explore how QuickDash accelerates your data workflow",
    headingUnit: "Features",
    cards: [
      {
        title: "Data Studio Reporting",
        description:
          "Connect common data sources like META, GA4, Google Ads, and LINE in just three minutes.",
      },
      {
        title: "Sheets Integration",
        description:
          "Sync raw data from all platforms directly to Google Sheets, eliminating manual exports.",
      },
      {
        title: "CLOUD MCP",
        description:
          "Leverage AI for data analysis and actionable insights to quickly boost ROAS.",
      },
      {
        title: "Data Monitoring",
        description: "Monitor and report ad performance in real-time via LINE.",
      },
      {
        title: "Competitor Analysis",
        description:
          "Extract competitor assets from the Ad Library to easily organize creatives and copywriting.",
      },
      {
        title: "Threads Monitoring",
        description:
          "Track relevant topics on Threads and analyze trending posts.",
      },
      {
        title: "Instagram Download",
        description: "Download images and videos directly from Instagram.",
      },
      {
        title: "BigQuery Warehousing",
        description:
          "Store data in BigQuery for highly efficient querying and analysis.",
      },
      {
        title: "META Audience",
        description:
          "Query META audience data for precise ad targeting and analysis.",
      },
      {
        title: "META Log Export",
        description:
          "Export META operation logs for seamless AI cross-analysis.",
      },
    ],
  },
  mcpShowcase: {
    eyebrow: "QuickDash MCP",
    headingBefore: "Ad insights in",
    headingAccent: "1",
    headingAfter: "question",
    subheading:
      "Ask in plain language inside Claude, ChatGPT or Gemini. QuickDash MCP pulls your ad data, charts it, and can even adjust your campaigns.",
    windowTitle: "QuickDash MCP",
    inputPlaceholder: "Ask about your ad data…",
    disclaimer: "*Illustrative demo. Conversation and figures are sample data.",
    scenarios: [
      {
        title: "Performance",
        description:
          "Ask which campaign has the best ROAS. The AI pulls the data and ranks it in a chart.",
        steps: [
          { kind: "user", text: "Which Meta campaign had the best ROAS last week?" },
          {
            kind: "tool",
            label: "Query Meta ad data",
            detail: "Campaign level · Last week",
          },
          {
            kind: "text",
            text: "Last week's top ROAS came from \"1111 Teaser_Conv\" at **4.8**: NT$32,400 in spend brought back NT$155,520 in revenue.",
          },
          {
            kind: "chart",
            chart: {
              kind: "ranking",
              title: "ROAS by campaign, last week",
              rows: [
                { label: "1111 Teaser_Conv", value: 4.8, display: "4.8" },
                { label: "New Launch_Retargeting", value: 3.6, display: "3.6" },
                { label: "Fall Collection_Broad", value: 2.3, display: "2.3" },
                { label: "Brand_Reach", value: 0.9, display: "0.9" },
              ],
            },
          },
          {
            kind: "text",
            text: "\"Brand_Reach\" sits at only **0.9**, but it's a reach campaign, so judge it by CPM and reach instead.",
          },
        ],
      },
      {
        title: "Cross-platform",
        description:
          "Compare Meta and Google Ads spend and conversions side by side. No more switching dashboards.",
        steps: [
          {
            kind: "user",
            text: "Compare Meta and Google Ads spend and conversions this month",
          },
          {
            kind: "tool",
            label: "Query Meta ad data",
            detail: "Account level · This month",
          },
          {
            kind: "tool",
            label: "Query Google Ads data",
            detail: "Account level · This month",
          },
          {
            kind: "text",
            text: "Combined spend this month is **NT$186,000**. Here's how they compare:",
          },
          {
            kind: "chart",
            chart: {
              kind: "compare",
              title: "This month: Meta vs Google Ads",
              series: ["Meta", "Google Ads"],
              rows: [
                {
                  label: "Spend",
                  values: [112000, 74000],
                  display: ["NT$112,000", "NT$74,000"],
                },
                {
                  label: "Conversions",
                  values: [640, 520],
                  display: ["640", "520"],
                },
                {
                  label: "CPA (lower is better)",
                  values: [175, 142],
                  display: ["NT$175", "NT$142"],
                },
                { label: "ROAS", values: [3.2, 4.1], display: ["3.2", "4.1"] },
              ],
            },
          },
          {
            kind: "text",
            text: "Google Ads' CPA is about **19%** lower than Meta's, with a higher ROAS. Consider shifting some budget to Google Search.",
          },
        ],
      },
      {
        title: "Take action",
        description:
          "Pause underperforming ad sets in one sentence. The AI lists them for your confirmation first.",
        steps: [
          {
            kind: "user",
            text: "Pause any ad sets with ROAS below 1 in the last 7 days",
          },
          {
            kind: "tool",
            label: "Query Meta ad data",
            detail: "Ad set level · Last 7 days",
          },
          {
            kind: "text",
            text: "Found **3** ad sets with ROAS below 1. Confirm and I'll pause them:",
          },
          {
            kind: "chart",
            chart: {
              kind: "status",
              title: "Ad sets with ROAS below 1",
              columns: ["Ad set", "ROAS", "7-day spend", "Status"],
              activeLabel: "Active",
              pausedLabel: "Paused",
              rows: [
                { label: "Fall_Interest A", roas: "0.62", spend: "NT$8,400" },
                { label: "Fall_Lookalike", roas: "0.78", spend: "NT$6,150" },
                { label: "Brand_Retargeting", roas: "0.91", spend: "NT$4,900" },
              ],
            },
          },
          { kind: "user", text: "Confirmed, pause them" },
          {
            kind: "tool",
            label: "Update ad status",
            detail: "3 ad sets → Paused",
            action: "pause",
          },
          {
            kind: "text",
            text: "Paused 3 ad sets, saving roughly **NT$19,450** a week in low-return spend.",
          },
        ],
      },
    ],
  },
  video: {
    heading: "Learn How to Integrate",
    subHeading: "Start Integrating Data in 3 Minutes",
    defaultTitle: "Data Studio Integration Tutorial",
  },
  plansPage: {
    metadataTitle: "Plans",
    heroTitleLine1: "Choose the Right",
    heroTitleLine2: "Integration Plan",
    heroSubtitle:
      "Whether you're an individual or a brand, we've got you supported.",
    additionalNotesTitle: "Additional Notes",
    additionalNotes: [
      "All plans include a 14-day free Data Studio integration trial with no credit card required.",
      "For enterprise-scale procurement, feel free to contact us via LINE.",
      "Connecting 1 Facebook Ads account + 1 Threads account counts as 2 linked accounts.",
      "Data analysis is powered by the advanced Claude AI model.",
      "High-volume mode is ideal for META ad spend above US$10,000/month or handling 50+ ad creatives at once.",
    ],
    sampleReportTitle: "View Full Report",
  },
  templatesPage: {
    metadataTitle: "Data Studio Templates",
    heroTitle: "Report Templates",
    heroDescription: "Data Studio Templates",
    ctaLabel: "Book a Template Demo",
    ctaHref: "https://line.me/R/ti/p/@070xojro",
    cards: [
      {
        category: "meta",
        typeLabel: "Meta",
        title: "Standard Report",
        description:
          "A comprehensive overview of Facebook ad performance and key metrics.",
        imageWidth: 1980,
        imageHeight: 1280,
        icon: "/icon/fb.png",
        image:
          "https://photo.quickdash.com.tw/banner/%E9%BB%91%E5%AE%A2-lading-page-14.jpg",
        href: "https://lookerstudio.google.com/u/0/reporting/25c98761-f852-4982-9ef5-73dc3ecd4e7b",
      },
      {
        category: "meta",
        typeLabel: "Meta",
        title: "Modern Report",
        description: "10+ must-see premium data dashboards.",
        imageWidth: 1980,
        imageHeight: 1280,
        icon: "/icon/fb.png",
        image:
          "https://photo.quickdash.com.tw/banner/%E7%8F%BE%E4%BB%A3%E7%89%88%E5%A0%B1%E5%91%8A.png",
        href: "https://lookerstudio.google.com/reporting/c102f677-7313-4680-905d-dc9e40e36092",
      },
      {
        category: "meta",
        typeLabel: "Meta",
        title: "E-commerce Report",
        description: "A premium report designed for e-commerce analysis.",
        imageWidth: 1980,
        imageHeight: 1280,
        icon: "/icon/fb.png",
        image: "https://photo.quickdash.com.tw/banner/%E9%9B%BB%E5%95%86.png",
        href: "https://lookerstudio.google.com/reporting/c198063e-8dbe-486f-87bb-d4a557c3d408",
      },
      {
        category: "meta",
        typeLabel: "Meta",
        title: "Non-E-commerce Report",
        description: "A premium report tailored for non-e-commerce businesses.",
        imageWidth: 1980,
        imageHeight: 1280,
        icon: "/icon/fb.png",
        image: "https://photo.quickdash.com.tw/banner/%E9%9B%BB%E5%95%86.png",
        href: "https://lookerstudio.google.com/reporting/781fd967-4576-4c1a-9896-26c1d194bbf9",
      },
      {
        category: "seo",
        typeLabel: "SEO",
        title: "Exclusive SEO Conversion Report",
        description: "Gain full visibility into SEO conversion performance.",
        icon: "/icon/gsc.png",
        imageWidth: 1980,
        imageHeight: 1280,
        image: "https://photo.quickdash.com.tw/quickdash-admin/seo-report.png",
        href: "https://lookerstudio.google.com/reporting/1e65ebfe-6c12-477f-b582-c9b51a245dc5",
      },
      {
        category: "multichannel",
        typeLabel: "Multi-Channel",
        title: "Exclusive Omni-Channel Report",
        description: "Contact QuickDash support to access the full report.",
        icon: "/wp-content/uploads/2025/10/network.png",
        imageWidth: 1980,
        imageHeight: 1280,
        image:
          "https://photo.quickdash.com.tw/quickdash-admin/Mutiple-report.png",
        href: "https://lookerstudio.google.com/reporting/c5e74d58-ebce-4a97-bfb8-d722c01ce8b1",
      },
      {
        category: "ga4",
        typeLabel: "GA4",
        title: "GA4 Premium Report",
        description: "Contact QuickDash support to access the full report.",
        icon: "/icon/ga4.png",
        imageWidth: 1980,
        imageHeight: 1280,
        image: "https://photo.quickdash.com.tw/quickdash-admin/GA4report.png",
        href: "https://lookerstudio.google.com/reporting/9f2f8b03-440a-4b4d-a4d0-8657e23e3703",
      },
      {
        category: "ga4",
        typeLabel: "GA4",
        title: "GA4 Landing Page Report",
        description: "Contact QuickDash support to access the full report.",
        icon: "/icon/ga4.png",
        imageWidth: 1980,
        imageHeight: 1280,
        image:
          "https://photo.quickdash.com.tw/quickdash-admin/ga4-landing-report.png",
        href: "https://lookerstudio.google.com/reporting/f8c293f7-c12e-4e45-ba68-861b9c7051e0",
      },
      {
        category: "ga4",
        typeLabel: "GA4",
        title: "GA4 Primary/Assist Conversion Report",
        description:
          "Track ad creative primary and assist conversion performance.",
        icon: "/icon/ga4.png",
        imageWidth: 1980,
        imageHeight: 1280,
        image: "https://photo.quickdash.com.tw/quickdash-admin/cv-report.png",
        href: "https://lookerstudio.google.com/reporting/5b8da08b-afc1-4ece-84ce-8abdff60c42e",
      },
    ],
  },
  mcpPage: {
    metadataTitle: "MCP Tool",
    hero: {
      title: "Let AI Read Your Ad Data Directly",
      ctaUrl: "https://ai.spyder.com.tw/mcp-web",
      ctaText: "Connect Report Now",
      youtubeId: "KWsLSVhtMqQ",
      youtubeTitle: "QuickDash MCP Tutorial",
    },
    aiTools: {
      title: "Supported AI Tools",
      description:
        "QuickDash MCP is compatible with leading AI tools — once installed, query your ad data right inside the AI assistant.",
      tools: [
        {
          name: "Claude",
          description: "Claude Desktop · Claude.ai",
          icon: "/icon/claude.png",
        },
        {
          name: "ChatGPT",
          description: "Custom Connectors",
          icon: "/icon/gpt.png",
        },
        { name: "Gemini", description: "Gemini CLI", icon: "/icon/gemini.png" },
      ],
    },
    dataSources: {
      title: "Supported Data Sources",
      description:
        "Connect the most common ad and e-commerce data sources in one place, synced automatically to your AI.",
      sources: [
        { name: "Meta Ads", icon: "/icon/fb.png" },
        { name: "Google Ads", icon: "/icon/gads.png" },
        { name: "GSC", icon: "/icon/gsc.png" },
        { name: "GA4", icon: "/icon/ga4.png" },
        { name: "LINE", icon: "/icon/line.png" },
        { name: "Microsoft Ads", icon: "/icon/bing.png" },
      ],
    },
    tools: {
      title: "Available MCP Tools",
      subtitle: "Call them directly from your AI.",
      groups: {
        meta: {
          name: "Meta Ads",
          items: [
            "Account lookup",
            "Performance data",
            "Custom conversions",
            "Campaign structure",
            "Adjust ad budget",
            "Toggle ad delivery",
            "Duplicate campaign",
            "Duplicate ad set",
            "Interest search",
            "Activity history",
            "Query Ad Creative enhanced",
            "Disable Ad Creative enhanced",
          ],
        },
        googleAds: {
          name: "Google Ads",
          items: ["Account lookup", "Performance data"],
        },
        ga4: {
          name: "GA4",
          items: [
            "Property lookup",
            "Analytics data",
            "Custom dimensions",
            "Custom metrics",
          ],
        },
        gsc: {
          name: "GSC",
          items: ["Site lookup", "Search performance"],
        },
        line: {
          name: "LINE",
          items: ["Account lookup", "Performance data"],
        },
        microsoft: {
          name: "Microsoft Ads",
          items: ["Account lookup", "Performance data"],
        },
      },
    },
  },
  tutorialPage: {
    metadataTitle: "Integration Tutorial",
    quickStart: {
      title: "Quick Start",
      description: "Import data into Data Studio in 3 minutes.",
      ctaUrl: "https://ai.spyder.com.tw/looker-web2",
      ctaText: "Connect Report Now",
      youtubeId: "zXtAPyQeMoE",
      youtubeTitle: "QuickDash Data Studio Tutorial",
    },
    steps: [
      {
        title: "1. Register an Account",
        description: "",
        emphasis: "Always free to register",
        image: {
          src: "/photos/login.png",
          alt: "Register account",
        },
        height: 400,
      },
      {
        title: "2. Connect Data Source",
        description:
          "Go to the data binding page\nand connect your data source.",
        image: {
          src: "/wp-content/uploads/2024/12/%E8%B3%87%E6%96%99%E7%B6%81%E5%AE%9A.jpg",
          alt: "Connect data source",
        },
        height: 400,
        width: 600,
      },
      {
        title: "3. Open Data Studio",
        description:
          "Copy the secret key from QuickDash\nand paste it into Data Studio for verification.",
        image: {
          src: "/wp-content/uploads/2025/10/%E7%99%BB%E5%85%A52.png",
          alt: "Verify in Data Studio",
        },
        height: 400,
        width: 600,
      },
      {
        title: "4. Done!",
        description: "Start integrating data and account sources.",
        image: {
          src: "/wp-content/uploads/2025/10/%E4%BF%AE%E6%94%B9.png",
          alt: "Completed setup",
        },
        height: 400,
        width: 600,
      },
    ],
    extraSections: [
      {
        title: "More Information",
        description: "After this tutorial, you are ready to import data!",
      },
      {
        title: "Binding Key",
        description:
          "This key is critical for verification.\nDo not share it with untrusted users.",
        emphasis:
          "QuickDash follows META policies.\nWe cannot modify or view detailed account data.",
        image: {
          src: "/wp-content/uploads/2024/12/%E8%B3%87%E6%96%99%E7%B6%81%E5%AE%9A2.jpg",
          alt: "Binding key",
        },
      },
      {
        title: "Setup Tip 1",
        description:
          "Pick any bound ad account.\nData Studio will display data for that account.",
        emphasis:
          "(Displayed account is synced with your bound QuickDash account)",
        image: {
          src: "/wp-content/uploads/2024/12/%E5%AE%8C%E6%88%90.jpg",
          alt: "Setup tip 1",
        },
      },
      {
        title: "Setup Tip 2",
        description:
          "After checking this option,\nyou can switch connected accounts in Data Studio.",
        image: {
          src: "/wp-content/uploads/2024/12/%E5%AE%8C%E6%88%90-2.jpg",
          alt: "Setup tip 2",
        },
      },
      {
        title: "Setup Tip 3",
        description: "You can switch attribution settings for META ad data.",
        image: {
          src: "/wp-content/uploads/2024/12/%E5%AE%8C%E6%88%90-3.jpg",
          alt: "Setup tip 3",
        },
      },
      {
        title: "Setup Tip 4",
        description:
          "You can directly edit META ad metrics.\n(CPC, purchase amount, ROAS, etc.)",
        image: {
          src: "/wp-content/uploads/2024/12/%E5%AE%8C%E6%88%90-5-2.jpg",
          alt: "Setup tip 4",
        },
      },
    ],
  },
  gsheetTutorialPage: {
    metadataTitle: "Google Sheets Integration Tutorial",
    quickStart: {
      title: "Quick Start",
      description: "Import data to Google Sheets in 3 minutes.",
      ctaUrl: "https://ai.spyder.com.tw/sheet-web2",
      ctaText: "Connect Ad Data",
      youtubeId: "Quxa5rb6nus",
      youtubeTitle: "QuickDash Google Sheet Tutorial",
    },
    steps: [
      {
        title: "1. Register an Account",
        description: "",
        emphasis: "Always free to connect",
        image: {
          src: "/photos/login.png",
          alt: "Register account",
        },
        height: 400,
      },
      {
        title: "2. Connect Data Source",
        description:
          "Go to the data binding page\nand connect your data source.",
        image: {
          src: "/wp-content/uploads/2024/12/%E8%B3%87%E6%96%99%E7%B6%81%E5%AE%9A.jpg",
          alt: "Connect data source",
        },
        height: 400,
      },
      {
        title: "3. Install Add-on",
        description: "",
        actionHref:
          "https://workspace.google.com/marketplace/app/%E5%BF%AB%E5%AE%A2_%E7%AC%AC%E4%B8%89%E6%96%B9%E4%B8%B2%E6%8E%A5%E5%B7%A5%E5%85%B7/663018989708",
        actionLabel: "Install Now",
        image: {
          src: "/wp-content/uploads/2025/09/1-1.png",
          alt: "Install add-on",
        },
        height: 320,
      },
      {
        title: "4. Open Spreadsheet",
        description:
          "Open Google Sheets.\nSelect Extensions and find QuickDash.\nThen click Run.",
        image: {
          src: "/wp-content/uploads/2025/05/7.jpg",
          alt: "Open spreadsheet and run QuickDash",
        },
        height: 260,
      },
      {
        title: "5. Choose Function",
        description: "Choose META Ads.",
        image: {
          src: "/wp-content/uploads/2025/05/5-1.png",
          alt: "Choose function",
        },
        height: 220,
      },
      {
        title: "6. Configure Ad Data",
        description:
          "Choose account, metrics, date range, attribution, etc.\nClick Generate Report. Done!",
        image: {
          src: "/wp-content/uploads/2025/05/6.png",
          alt: "Configure ad data",
        },
        height: 420,
      },
    ],
    extraSections: [
      {
        title: "Extra Features",
        description: "After this tutorial, you are ready to import data!",
      },
      {
        title: "Binding Key",
        description:
          "This key is critical for verification.\nDo not share it with untrusted users.",
        emphasis:
          "QuickDash follows META policies.\nWe cannot modify or view detailed account data.",
        image: {
          src: "/wp-content/uploads/2024/12/%E8%B3%87%E6%96%99%E7%B6%81%E5%AE%9A2.jpg",
          alt: "Binding key",
        },
      },
      {
        title: "Saved Reports",
        description:
          "Save report presets for faster access to previous account and metric settings.",
        image: {
          src: "/wp-content/uploads/2025/06/%E5%84%B2%E5%AD%98%E5%A0%B1%E5%91%8A.png",
          alt: "Saved reports",
        },
        height: 420,
        width: 280,
      },
      {
        title: "Automation Schedule",
        description:
          "Supports automatic updates with Overwrite or Append mode.",
        image: {
          src: "/wp-content/uploads/2025/06/%E6%8E%92%E7%A8%8B.png",
          alt: "Automation schedule",
        },
        height: 340,
        width: 280,
      },
      {
        title: "Automated Email",
        description:
          "Convert reports in Sheets to PDF and send them to target email addresses.",
        emphasis: "",
        image: {
          src: "/wp-content/uploads/2025/06/%E7%99%BC%E4%BF%A1.png",
          alt: "Automated email",
        },
        height: 420,
        width: 280,
      },
    ],
  },
  extensionTutorialPage: {
    metadataTitle: "Chrome Ext Tutorial",
    quickStart: {
      title: "Quick Start",
      description: "Start collecting ad creatives and complete setup quickly.",
      ctaUrl: "https://ai.spyder.com.tw/yt-web9/web",
      ctaText: "Collect Ad Creatives",
      youtubeId: "Gj3jdnO3Uco",
      youtubeTitle: "QuickDash Chrome Extension Tutorial",
    },
    toolsSection: {
      title: "Multiple Extension Features",
      description: "Once installed, all of these features are available.",
      tools: [
        {
          name: "Ad Performance Reports",
          description:
            "Export ad performance from every platform and build reports in one click.",
        },
        {
          name: "Hidden Audience Lookup",
          description:
            "Find the interest audiences Meta no longer lists in its ad manager.",
        },
        {
          name: "SEO Site Crawler",
          description:
            "Crawl titles, descriptions and tag structure for a fast SEO health check.",
        },
        {
          name: "GA4 Setup Checker",
          description:
            "Verify that GA4 is installed correctly and inspect event delivery.",
        },
        {
          name: "GTM Event Setup",
          description:
            "Deploy GTM tags and event tracking quickly, without tedious configuration.",
        },
        {
          name: "E-commerce Platform Lookup",
          description:
            "Detect which e-commerce platform and front-end stack a site runs on.",
        },
        {
          name: "Meta Creative Export",
          description:
            "Export the original image and video files from Meta ads.",
        },
        {
          name: "IG Photo & Video Download",
          description: "Download images and videos from Instagram posts.",
        },
        {
          name: "Ad Creative Export",
          description:
            "Batch-export ad creatives together with their performance data.",
        },
        {
          name: "Ad Change Log Export",
          description:
            "Export the change history and activity log of an ad account.",
        },
      ],
    },
    steps: [
      {
        title: "1. Register an Account",
        description: "",
        emphasis: "Always free to connect",
        image: {
          src: "/photos/login.png",
          alt: "Register account",
        },
        height: 400,
      },
      {
        title: "2. Connect Data Source",
        description:
          "Go to the data binding page\nand connect your data source.",
        image: {
          src: "/wp-content/uploads/2024/12/%E8%B3%87%E6%96%99%E7%B6%81%E5%AE%9A.jpg",
          alt: "Connect data source",
        },
        height: 400,
      },
      {
        title: "3. Install Extension",
        description:
          "Install the QuickDash Chrome extension from Chrome Web Store.",
        actionHref:
          "https://chromewebstore.google.com/detail/dagdchpdhalojgcfnhbnmkcbjoggbclb",
        actionLabel: "Install Now",
      },
      {
        title: "4. Open Extension",
        description:
          "At the top-right of the browser,\nclick the extension (Our Logo Icon).",
        image: {
          src: "/icon/logo.png",
          alt: "Open extension",
        },
        height: 100,
      },
      {
        title: "5. Click Login",
        description: "From the left menu, click QuickDash Login.",
        image: {
          src: "/wp-content/uploads/2025/05/c3.png",
          alt: "QuickDash login",
        },
        height: 420,
      },
      {
        title: "6. Paste QuickDash Key",
        description: "Paste your key from QuickDash backend.",
        image: {
          src: "/wp-content/uploads/2025/05/c5.png",
          alt: "Paste QuickDash key",
        },
        height: 320,
      },
      {
        title: "7. Completed",
        description:
          "Start data extraction\nHidden audience lookup\nCreative export and more!",
        image: {
          src: "/wp-content/uploads/2025/05/c6.png",
          alt: "Completed",
        },
        height: 420,
      },
    ],
    extraSections: [
      {
        title: "More Information",
        description: "After this tutorial, you are ready to import data!",
      },
      {
        title: "Binding Key",
        description:
          "This key is critical for verification.\nDo not share it with untrusted users.",
        emphasis:
          "QuickDash follows META policies.\nWe cannot modify or view detailed account data.",
        image: {
          src: "/wp-content/uploads/2024/12/%E8%B3%87%E6%96%99%E7%B6%81%E5%AE%9A2.jpg",
          alt: "Binding key",
        },
      },
    ],
  },
  metaAdsPage: {
    metadataTitle: "Meta Ads",
    heroTitle: "META Ads Integration",
    heroDescriptionLine1: "Automatically send data to third-party platforms.",
    heroDescriptionLine2:
      "Supports Google Sheets, Data Studio, and Chrome Extension.",
    ctaLabel: "Available Metrics",
    supportedQueriesTitle: "Supported Queries",
    supportedQueries: [
      "Ad Performance",
      "E-commerce Performance",
      "Audience Performance",
      "Ad Creatives",
      "ROAS",
    ],
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-2.gif",
        alt: "Chrome Extension",
      },
      {
        src: "/icon/claude.png",
        alt: "MCP AI Analysis",
      },
    ],
    quickdashSectionTitle: "Meta Ads Integration",
    quickdashSectionDescription:
      "A high-efficiency reporting helper for building diverse dashboards.",
    featureCards: [
      {
        title: "Automated Reports",
        description: "Reports update automatically on schedule.",
      },
      {
        title: "Quick Data Cleanup",
        description: "Transform raw data into readable reports.",
      },
      {
        title: "Multiple Channels",
        description:
          "Supports Google Sheets, Chrome Extension, and Data Studio.",
      },
    ],
    stepsTitle: "4 Steps to Connect META Ads",
    steps: [
      {
        title: "1. Register an Account",
        description:
          "Create your account. QuickDash offers a permanent free trial.",
        image: {
          src: "/photos/login.png",
          alt: "Register account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. Bind Account",
        description: "Bind the Meta Ads account you want to connect.",
        image: {
          src: "/wp-content/uploads/2025/07/5.png",
          alt: "Bind account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. Paste Secret Key",
        description:
          "Paste the key to your target destination (Google Sheets / Data Studio).",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Paste secret key",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. Complete Import",
        description: "You are ready to import data and build reports.",
        image: {
          src: "/wp-content/uploads/2025/08/18.png",
          alt: "Complete import",
          width: 760,
          height: 480,
        },
      },
    ],
    notesTitle: "Notes",
    notesDescription:
      "To connect META ad data, please refer to the setup examples below:",
    notesImages: [
      {
        src: "/wp-content/uploads/2025/04/16.png",
        alt: "Meta Ads permission setup",
        caption: "Open settings for the target ad account",
      },
      {
        src: "/wp-content/uploads/2025/08/landing.png",
        alt: "Integration example",
        caption: "Requires ad account permission: View Performance",
      },
    ],
  },
  igInsightsPage: {
    metadataTitle: "Instagram Insights",
    heroTitle: "Instagram",
    heroDescriptionLine1: "Automatically send data to third-party platforms.",
    heroDescriptionLine2:
      "Supports Google Sheets, Data Studio, and Chrome Extension.",
    ctaLabel: "Available Metrics",
    supportedQueriesTitle: "Supported Queries",
    supportedQueries: [
      "Post Image",
      "Post Link",
      "Account Performance",
      "Reels Performance",
    ],
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
    ],
    quickdashSectionTitle: "Instagram Insights Integration",
    quickdashSectionDescription:
      "A high-efficiency reporting helper for building diverse dashboards.",
    featureCards: [
      {
        title: "Automated Reports",
        description: "Reports update automatically on schedule.",
      },
      {
        title: "Quick Data Cleanup",
        description: "Transform raw data into readable reports.",
      },
      {
        title: "Multiple Channels",
        description:
          "Supports Google Sheets, Chrome Extension, and Data Studio.",
      },
    ],
    stepsTitle: "4 Steps to Connect Instagram Insights",
    steps: [
      {
        title: "1. Register an Account",
        description:
          "Create your account. QuickDash offers a permanent free trial.",
        image: {
          src: "/photos/login.png",
          alt: "Register account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. Bind Account",
        description: "Bind the IG account you want to connect.",
        image: {
          src: "/wp-content/uploads/2025/07/3.png",
          alt: "Bind account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. Paste Secret Key",
        description:
          "Paste the key to your target destination (Google Sheets / Data Studio).",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Paste secret key",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. Complete Import",
        description: "You are ready to import data and build reports.",
        image: {
          src: "/wp-content/uploads/2025/08/15.png",
          alt: "Complete import",
          width: 760,
          height: 480,
        },
      },
    ],
    notesTitle: "Notes",
    notesDescription:
      "To connect Instagram social data, please ensure the following setup is complete:",
    notesChecklist: [
      "IG must be connected to a Facebook Page",
      "Facebook Page must be connected to Business Manager (BM)",
      "You need Insights permission for that Facebook Page",
    ],
    notesImages: [
      {
        src: "/wp-content/uploads/2025/04/9.png",
        alt: "IG connected to FB Page",
        caption: "1. IG must be connected to a Facebook Page",
      },
      {
        src: "/wp-content/uploads/2025/09/21-1.png",
        alt: "Page connected to BM",
        caption: "2. Facebook Page connected to Business Manager (BM)",
      },
      {
        src: "/wp-content/uploads/2025/04/11.png",
        alt: "Insights permission",
        caption: "3. You need Insights permission for that page",
      },
    ],
  },
  threadsPage: {
    metadataTitle: "Threads",
    heroTitle: "Threads",
    heroDescriptionLine1: "Automatically send data to third-party platforms.",
    heroDescriptionLine2:
      "Supports Google Sheets, Data Studio, and Chrome Extension.",
    ctaLabel: "Available Metrics",
    ctaHref: "/articles/thread-metrics/",
    supportedQueriesTitle: "Supported Queries",
    supportedQueries: [
      "Post Image",
      "Post Link",
      "Account Performance",
      "Reach Trend",
    ],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-2.gif",
        alt: "Chrome Extension",
      },
    ],
    heroImage: {
      src: "/wp-content/uploads/2025/02/THREAD.png",
      alt: "Threads",
      width: 240,
      height: 240,
    },
    quickdashSectionTitle: "Threads Integration",
    quickdashSectionDescription:
      "A high-efficiency reporting helper for building diverse dashboards.",
    featureCards: [
      {
        title: "Automated Reports",
        description: "Reports update automatically on schedule.",
      },
      {
        title: "Quick Data Cleanup",
        description: "Transform raw data into readable reports.",
      },
      {
        title: "Multiple Channels",
        description:
          "Supports Google Sheets, Chrome Extension, and Data Studio.",
      },
    ],
    stepsTitle: "4 Steps to Connect Threads Insights",
    steps: [
      {
        title: "1. Register an Account",
        description:
          "Create your account. QuickDash offers a permanent free trial.",
        image: {
          src: "/photos/login.png",
          alt: "Register account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. Bind Account",
        description: "Bind the Threads account you want to connect.",
        image: {
          src: "/wp-content/uploads/2025/07/4.png",
          alt: "Bind account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. Paste Secret Key",
        description:
          "Paste the key to your target destination (Google Sheets / Data Studio).",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Paste secret key",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. Complete Import",
        description: "You are ready to import data and build reports.",
        image: {
          src: "/wp-content/uploads/2025/08/16.png",
          alt: "Complete import",
          width: 760,
          height: 480,
        },
      },
    ],
    notesTitle: "Notes",
    notesDescription:
      "To connect Threads social data, please complete the following setup:",
    notesChecklist: [
      "Sign in to Threads on a desktop browser",
      "Bind Threads in QuickDash",
      "Only one Threads account can be bound at a time",
    ],
    notesImages: [
      {
        src: "/wp-content/uploads/2025/05/20.png",
        alt: "Threads login example",
        caption: "Sign in to Threads on a desktop browser",
      },
      {
        src: "/wp-content/uploads/2025/05/21.png",
        alt: "QuickDash Threads binding example",
        caption: "Bind Threads in QuickDash",
      },
    ],
    notesFrameHeight: 700,
  },
  lapPage: {
    metadataTitle: "Line Lap",
    heroTitle: "LINE Lap",
    heroTitleLine2: "Ads Integration",
    heroDescriptionLine1: "Automatically send data to third-party platforms.",
    heroDescriptionLine2: "Supports Google Sheets and Data Studio.",
    supportedQueriesTitle: "Supported Queries",
    supportedQueries: [
      "E-commerce Performance",
      "App Performance",
      "Game Performance",
    ],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-3",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
      {
        src: "/icon/claude.png",
        alt: "MCP AI Analysis",
      },
    ],
    heroImage: {
      src: "/wp-content/uploads/2025/03/line-1.png",
      alt: "Line Lap",
      width: 220,
      height: 220,
    },
    quickdashSectionTitle: "LINE LAP Integration",
    quickdashSectionDescription:
      "A high-efficiency reporting helper for building diverse dashboards.",
    featureCards: [
      {
        title: "Automated Reports",
        description: "Reports update automatically on schedule.",
      },
      {
        title: "Quick Data Cleanup",
        description: "Transform raw data into readable reports.",
      },
      {
        title: "Multiple Channels",
        description: "Supports Google Sheets and Data Studio.",
      },
    ],
    stepsTitle: "4 Steps to Connect LINE LAP",
    steps: [
      {
        title: "1. Register an Account",
        description:
          "Create your account. QuickDash offers a permanent free trial.",
        image: {
          src: "/photos/login.png",
          alt: "Register account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. Bind Account",
        description: "Bind your LINE LAP ad account from GOD Digital.",
        image: {
          src: "/wp-content/uploads/2025/07/2.png",
          alt: "Bind account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. Paste Secret Key",
        description:
          "Paste the key to your target destination (Google Sheets / Data Studio).",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Paste secret key",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. Complete Import",
        description: "You are ready to import data and build reports.",
        image: {
          src: "/wp-content/uploads/2025/08/11-2.png",
          alt: "Complete import",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  metaPagePage: {
    metadataTitle: "Meta Page",
    heroTitle: "META Page",
    heroDescriptionLine1: "Automatically send data to third-party platforms.",
    heroDescriptionLine2:
      "Supports Google Sheets, Data Studio, and Chrome Extension.",
    ctaLabel: "Available Metrics",
    ctaHref: "/articles/metapage-metrics/",
    supportedQueriesTitle: "Supported Queries",
    supportedQueries: [
      "Post Image",
      "Post Link",
      "Post Copy",
      "Post Performance",
    ],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-2.gif",
        alt: "Chrome Extension",
      },
    ],
    heroImage: {
      src: "/wp-content/uploads/2025/04/%E5%BF%AB%E5%AE%A2-Gsheet-%E9%A6%96%E9%A0%81icon.png",
      alt: "Meta Page",
      width: 240,
      height: 240,
    },
    quickdashSectionTitle: "Meta Page Integration",
    quickdashSectionDescription:
      "A high-efficiency reporting helper for building diverse dashboards.",
    featureCards: [
      {
        title: "Automated Reports",
        description: "Reports update automatically on schedule.",
      },
      {
        title: "Quick Data Cleanup",
        description: "Transform raw data into readable reports.",
      },
      {
        title: "Multiple Channels",
        description:
          "Supports Google Sheets, Chrome Extension, and Data Studio.",
      },
    ],
    stepsTitle: "4 Steps to Connect META Page",
    steps: [
      {
        title: "1. Register an Account",
        description:
          "Create your account. QuickDash offers a permanent free trial.",
        image: {
          src: "/photos/login.png",
          alt: "Register account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. Bind Account",
        description: "Bind the Facebook Page you want to connect.",
        image: {
          src: "/wp-content/uploads/2025/07/landing-1-1.png",
          alt: "Bind page",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. Paste Secret Key",
        description:
          "Paste the key to your target destination (Google Sheets / Data Studio).",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Paste secret key",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. Complete Import",
        description: "You are ready to import data and build reports.",
        image: {
          src: "/wp-content/uploads/2025/07/14.png",
          alt: "Complete import",
          width: 760,
          height: 480,
        },
      },
    ],
    notesTitle: "Notes",
    notesDescription:
      "To connect Facebook Page data, ensure the following setup is complete:",
    notesChecklist: [
      "Facebook Page must be connected to Business Manager (BM)",
      "You need Insights permission for that page",
    ],
    notesImages: [
      {
        src: "/wp-content/uploads/2025/09/20.png",
        alt: "Page connected to BM",
        caption: "Facebook Page must be connected to Business Manager (BM)",
      },
      {
        src: "/wp-content/uploads/2025/04/14.png",
        alt: "Insights permission",
        caption: "You need Insights permission for that page",
      },
    ],
    notesFrameHeight: 700,
  },
  gaPage: {
    metadataTitle: "GA4",
    heroTitle: "Google Analytics 4",
    heroDescriptionLine1: "Automatically send data to third-party platforms.",
    heroDescriptionLine2: "Supports Google Sheets and Data Studio.",
    ctaLabel: "Available Metrics",
    ctaHref: "/articles/ga-options/",
    supportedQueriesTitle: "Supported Queries",
    supportedQueries: [
      "Event Reports",
      "Landing Pages",
      "Traffic Acquisition",
      "New User Acquisition",
      "Page Views",
      "E-commerce",
      "E-commerce Funnel",
      "Demographics",
    ],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
      {
        src: "/icon/claude.png",
        alt: "MCP AI Analysis",
      },
    ],
    heroImage: {
      src: "/wp-content/uploads/2025/07/ga-2.png",
      alt: "Google Analytics 4",
      width: 260,
      height: 260,
      imageClassName: "w-44 h-44 sm:w-56 sm:h-56 object-contain",
    },
    quickdashSectionTitle: "GA4 Integration",
    quickdashSectionDescription:
      "A high-efficiency reporting helper for building diverse dashboards.",
    featureCards: [
      {
        title: "Automated Reports",
        description: "Reports update automatically on schedule.",
      },
      {
        title: "Quick Data Cleanup",
        description: "Transform raw data into readable reports.",
      },
      {
        title: "Multiple Channels",
        description: "Supports Google Sheets and Data Studio.",
      },
    ],
    stepsTitle: "4 Steps to Connect GA4",
    steps: [
      {
        title: "1. Register an Account",
        description:
          "Create your account. QuickDash offers a permanent free trial.",
        image: {
          src: "/photos/login.png",
          alt: "Register account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. Bind Account",
        description: "Bind the GA4 property you want to connect.",
        image: {
          src: "/wp-content/uploads/2025/07/landing-1.png",
          alt: "Bind account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. Paste Secret Key",
        description:
          "Paste the key to your target destination (Google Sheets / Data Studio).",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Paste secret key",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. Complete Import",
        description: "You are ready to import data and build reports.",
        image: {
          src: "/wp-content/uploads/2025/11/14.png",
          alt: "Complete import",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  gadsPage: {
    metadataTitle: "Google Ads",
    heroTitle: "Google Ads",
    heroDescriptionLine1: "Automatically send data to third-party platforms.",
    heroDescriptionLine2: "Supports Google Sheets and Data Studio.",
    ctaLabel: "Available Metrics",
    ctaHref: "/articles/gads-metrics/",
    supportedQueriesTitle: "Supported Queries",
    supportedQueries: [
      "Campaign Report",
      "Ad Group Report",
      "Dynamic Search Report",
      "Bidding Strategy Report",
      "Account Report",
      "Click Report",
      "Engagement Report",
      "Video Report",
      "Keyword Report",
      "Search Terms Report",
      "Device Report",
      "Age Report",
      "Gender Report",
      "Geography Report",
      "Impression Share Report",
      "GAds + GA4 Report",
      "Conversion Report",
    ],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
      {
        src: "/icon/claude.png",
        alt: "MCP AI Analysis",
      },
    ],
    heroImage: {
      src: "/wp-content/uploads/2025/06/%E6%9C%AA%E5%91%BD%E5%90%8D%E8%A8%AD%E8%A8%88.png",
      alt: "Google Ads",
      width: 240,
      height: 240,
    },
    quickdashSectionTitle: "Google Ads Integration",
    quickdashSectionDescription:
      "A high-efficiency reporting helper for building diverse dashboards.",
    featureCards: [
      {
        title: "Automated Reports",
        description: "Reports update automatically on schedule.",
      },
      {
        title: "Quick Data Cleanup",
        description: "Transform raw data into readable reports.",
      },
      {
        title: "Multiple Channels",
        description: "Supports Google Sheets and Data Studio.",
      },
    ],
    stepsTitle: "4 Steps to Connect Google Ads",
    steps: [
      {
        title: "1. Register an Account",
        description:
          "Create your account. QuickDash offers a permanent free trial.",
        image: {
          src: "/photos/login.png",
          alt: "Register account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. Bind Account",
        description: "Bind the Google Ads account you want to connect.",
        image: {
          src: "/wp-content/uploads/2025/08/9-2.png",
          alt: "Bind account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. Paste Secret Key",
        description: "Paste the key to Google Sheets or Data Studio.",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Paste secret key",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. Complete Import",
        description: "You are ready to import data and build reports.",
        image: {
          src: "/wp-content/uploads/2025/08/13-1.png",
          alt: "Complete import",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  bingPage: {
    metadataTitle: "Microsoft Ads",
    heroTitle: "Microsoft Ads",
    heroDescriptionLine1: "Automatically send data to third-party platforms.",
    heroDescriptionLine2: "Supports Google Sheets and Data Studio.",
    ctaLabel: "Available Metrics",
    ctaHref: "/articles/microsoft-metrics/",
    supportedQueriesTitle: "Supported Queries",
    supportedQueries: [
      "Account Report",
      "Campaign Report",
      "Ad Group Report",
      "Ad Report",
      "Keyword Report",
      "Search Terms Report",
      "Device Report",
      "Age & Gender Report",
      "Geography Report",
      "Impression Share Report",
      "Asset Group Report (PMax)",
      "Conversion Report",
    ],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
    ],
    heroImage: {
      src: "/icon/bing.png",
      alt: "Microsoft Ads",
      width: 240,
      height: 240,
    },
    quickdashSectionTitle: "Microsoft Ads Integration",
    quickdashSectionDescription:
      "A high-efficiency reporting helper for building diverse dashboards.",
    featureCards: [
      {
        title: "Automated Reports",
        description: "Reports update automatically on schedule.",
      },
      {
        title: "Quick Data Cleanup",
        description: "Transform raw data into readable reports.",
      },
      {
        title: "Multiple Channels",
        description: "Supports Google Sheets and Data Studio.",
      },
    ],
    stepsTitle: "4 Steps to Connect Microsoft Ads",
    steps: [
      {
        title: "1. Register an Account",
        description:
          "Create your account. QuickDash offers a permanent free trial.",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "Register account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. Bind Account",
        description: "Bind the Microsoft Ads account you want to connect.",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "Bind account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. Paste Secret Key",
        description: "Paste the key to Google Sheets or Data Studio.",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Paste secret key",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. Complete Import",
        description: "You are ready to import data and build reports.",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Complete import",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  gbpPage: {
    metadataTitle: "Google Business Profile",
    heroTitle: "Google Business Profile",
    heroDescriptionLine1: "Automatically send data to third-party platforms.",
    heroDescriptionLine2: "Supports Google Sheets and Data Studio.",
    supportedQueriesTitle: "Supported Queries",
    supportedQueries: [
      "Location",
      "Time",
      "Business Performance",
      "Search Keywords",
      "Review Details",
      "Review Summary",
      "Current Rating",
      "Business Info",
    ],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
      {
        src: "/icon/claude.png",
        alt: "MCP AI Analysis",
      },
    ],
    heroImage: {
      src: "/icon/GBP.png",
      alt: "Google Business Profile",
      width: 240,
      height: 240,
    },
    quickdashSectionTitle: "Google Business Profile Integration",
    quickdashSectionDescription:
      "A high-efficiency reporting helper for building diverse dashboards.",
    featureCards: [
      {
        title: "Automated Reports",
        description: "Reports update automatically on schedule.",
      },
      {
        title: "Quick Data Cleanup",
        description: "Transform raw data into readable reports.",
      },
      {
        title: "Multiple Channels",
        description: "Supports Google Sheets and Data Studio.",
      },
    ],
    stepsTitle: "4 Steps to Connect Google Business Profile",
    steps: [
      {
        title: "1. Register an Account",
        description:
          "Create your account. QuickDash offers a permanent free trial.",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "Register account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. Bind Account",
        description:
          "Bind the Google Business Profile account you want to connect.",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "Bind account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. Paste Secret Key",
        description: "Paste the key to Google Sheets or Data Studio.",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Paste secret key",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. Complete Import",
        description: "You are ready to import data and build reports.",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Complete import",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  openaiPage: {
    metadataTitle: "OpenAI Ads",
    heroTitle: "OpenAI Ads",
    heroDescriptionLine1: "Automatically send data to third-party platforms.",
    heroDescriptionLine2: "Supports Google Sheets, Data Studio and MCP.",
    ctaLabel: "可查詢指標",
    ctaHref: "/articles/oai-metrics/",
    supportedQueriesTitle: "Supported Queries",
    supportedQueries: [
      "Account Report",
      "Campaign Report",
      "Ad Group Report",
      "Ad Report",
      "Creative Report",
      "Daily Performance Report",
      "Country Report",
      "Device Report",
      "Platform Report",
      "Product Report",
      "Conversion Event Report",
    ],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
      {
        src: "/icon/claude.png",
        alt: "MCP AI Analysis",
      },
    ],
    heroImage: {
      src: "/icon/openai.png",
      alt: "OpenAI Ads",
      width: 240,
      height: 240,
    },
    quickdashSectionTitle: "OpenAI Ads Integration",
    quickdashSectionDescription:
      "A high-efficiency reporting helper for building diverse dashboards.",
    featureCards: [
      {
        title: "Automated Reports",
        description: "Reports update automatically on schedule.",
      },
      {
        title: "Quick Data Cleanup",
        description: "Transform raw data into readable reports.",
      },
      {
        title: "Multiple Channels",
        description: "Supports Google Sheets and Data Studio.",
      },
    ],
    stepsTitle: "4 Steps to Connect OpenAI Ads",
    steps: [
      {
        title: "1. Register an Account",
        description:
          "Create your account. QuickDash offers a permanent free trial.",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "Register account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. Bind Account",
        description: "Bind the OpenAI Ads account you want to connect.",
        image: {
          src: "/wp-content/uploads/2025/09/1.png",
          alt: "Bind account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. Paste Secret Key",
        description: "Paste the key to Google Sheets or Data Studio.",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Paste secret key",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. Complete Import",
        description: "You are ready to import data and build reports.",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Complete import",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  gscPage: {
    metadataTitle: "GSC",
    heroTitle: "Search Console",
    heroDescriptionLine1: "Automatically send data to third-party platforms.",
    heroDescriptionLine2: "Supports Google Sheets and Data Studio.",
    ctaLabel: "Available Metrics",
    ctaHref: "/articles/gsc-metrics/",
    supportedQueriesTitle: "Supported Queries",
    supportedQueries: [
      "Website Ranking",
      "Website Clicks",
      "Website CTR",
      "Website Impressions",
    ],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
      {
        src: "/icon/claude.png",
        alt: "MCP AI Analysis",
      },
    ],
    heroImage: {
      src: "/wp-content/uploads/2025/09/%E6%9C%AA%E5%91%BD%E5%90%8D%E8%A8%AD%E8%A8%88.png",
      alt: "Search Console",
      width: 240,
      height: 240,
    },
    quickdashSectionTitle: "Search Console Integration",
    quickdashSectionDescription:
      "A high-efficiency reporting helper for building diverse dashboards.",
    featureCards: [
      {
        title: "Automated Reports",
        description: "Reports update automatically on schedule.",
      },
      {
        title: "Quick Data Cleanup",
        description: "Transform raw data into readable reports.",
      },
      {
        title: "Multiple Channels",
        description: "Supports Google Sheets and Data Studio.",
      },
    ],
    stepsTitle: "4 Steps to Connect GSC",
    steps: [
      {
        title: "1. Register an Account",
        description:
          "Create your account. QuickDash offers a permanent free trial.",
        image: {
          src: "/photos/login.png",
          alt: "Register account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. Bind Account",
        description: "Bind the GSC site you want to connect.",
        image: {
          src: "/wp-content/uploads/2025/07/7.png",
          alt: "Bind account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. Paste Secret Key",
        description:
          "Paste the key to your target destination (Google Sheets / Data Studio).",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Paste secret key",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. Complete Import",
        description: "You are ready to import data and build reports.",
        image: {
          src: "/wp-content/uploads/2025/08/12.png",
          alt: "Complete import",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  cyberbizPage: {
    metadataTitle: "CYBERBIZ",
    heroTitle: "CYBERBIZ",
    heroDescriptionLine1: "Automatically send data to third-party platforms.",
    heroDescriptionLine2: "Supports Google Sheets and Data Studio.",
    ctaLabel: "Available Metrics",
    ctaHref: "/articles/cyberbiz-metrics/",
    supportedQueriesTitle: "Supported Queries",
    supportedQueries: ["Member Data", "Order Data"],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
    ],
    heroImage: {
      src: "/wp-content/uploads/2025/11/568981694_1134786405497881_6970468732146188645_n.jpg",
      alt: "CYBERBIZ",
      width: 320,
      height: 320,
      imageClassName: "rounded-2xl w-48 h-48 sm:w-64 sm:h-64 object-contain",
    },
    quickdashSectionTitle: "CYBERBIZ Integration",
    quickdashSectionDescription:
      "A high-efficiency reporting helper for building diverse dashboards.",
    featureCards: [
      {
        title: "Automated Reports",
        description: "Reports update automatically on schedule.",
      },
      {
        title: "Quick Data Cleanup",
        description: "Transform raw data into readable reports.",
      },
      {
        title: "Multiple Channels",
        description: "Supports Google Sheets and Data Studio.",
      },
    ],
    stepsTitle: "4 Steps to Connect CYBERBIZ",
    steps: [
      {
        title: "1. Register an Account",
        description:
          "Create your account. QuickDash offers a permanent free trial.",
        image: {
          src: "/photos/login.png",
          alt: "Register account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. Bind Account",
        description:
          "Bind your CYBERBIZ account. After binding, QuickDash app is created automatically in CYBERBIZ APP Market.",
        image: {
          src: "/wp-content/uploads/2025/11/9.png",
          alt: "Bind account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. Paste Secret Key",
        description:
          "Paste the key to your target destination (Google Sheets / Data Studio).",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Paste secret key",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. Complete Import",
        description: "You are ready to import data and build reports.",
        image: {
          src: "/wp-content/uploads/2025/11/landing.png",
          alt: "Complete import",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  shopifyPage: {
    metadataTitle: "SHOPIFY",
    heroTitle: "SHOPIFY",
    heroDescriptionLine1: "Automatically send data to third-party platforms.",
    heroDescriptionLine2: "Supports Google Sheets and Data Studio.",
    ctaLabel: "Available Metrics",
    ctaHref: "/articles/shopify-metrics/",
    supportedQueriesTitle: "Supported Queries",
    supportedQueries: [
      "Member Data",
      "Order Data",
      "Order Attribution",
      "Product Data",
    ],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
    ],
    heroImage: {
      src: "/wp-content/uploads/2025/11/1865631fe5aad6ba138c6d83dc42aa70.png",
      alt: "SHOPIFY",
      width: 320,
      height: 320,
      wrapperClassName:
        "rounded-2xl border border-white/40 flex items-center justify-center",
      imageClassName: "w-48 h-48 sm:w-64 sm:h-64 object-contain",
    },
    quickdashSectionTitle: "SHOPIFY Integration",
    quickdashSectionDescription:
      "A high-efficiency reporting helper for building diverse dashboards.",
    featureCards: [
      {
        title: "Automated Reports",
        description: "Reports update automatically on schedule.",
      },
      {
        title: "Quick Data Cleanup",
        description: "Transform raw data into readable reports.",
      },
      {
        title: "Multiple Channels",
        description: "Supports Google Sheets and Data Studio.",
      },
    ],
    stepsTitle: "4 Steps to Connect SHOPIFY",
    steps: [
      {
        title: "1. Register an Account",
        description:
          "Create your account. QuickDash offers a permanent free trial. This connector requires the Premium plan.",
        image: {
          src: "/photos/login.png",
          alt: "Register account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. Bind Account",
        description:
          "Bind your SHOPIFY account. After binding, QuickDash app is created automatically in SHOPIFY App Market.",
        image: {
          src: "/wp-content/uploads/2025/12/landing.png",
          alt: "Bind account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. Paste Secret Key",
        description:
          "Paste the key to your target destination (Google Sheets / Data Studio).",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Paste secret key",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. Complete Import",
        description: "You are ready to import data and build reports.",
        image: {
          src: "/wp-content/uploads/2025/12/landing-1.png",
          alt: "Complete import",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  shoplinePage: {
    metadataTitle: "SHOPLINE",
    heroTitle: "SHOPLINE",
    heroDescriptionLine1: "Automatically send data to third-party platforms.",
    heroDescriptionLine2: "Supports Google Sheets and Data Studio.",
    ctaLabel: "Available Metrics",
    ctaHref: "/articles/shopline-metrics/",
    supportedQueriesTitle: "Supported Queries",
    supportedQueries: [
      "Member Data",
      "Order Data",
      "Product Data",
      "Promotion Data",
    ],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
    ],
    heroImage: {
      src: "/icon/shopline.jpg",
      alt: "SHOPLINE",
      width: 320,
      height: 320,
      wrapperClassName:
        "rounded-2xl border border-white/40 flex items-center justify-center",
      imageClassName: "w-48 h-48 sm:w-64 sm:h-64 rounded-xl object-contain",
    },
    quickdashSectionTitle: "SHOPLINE Integration",
    quickdashSectionDescription:
      "A high-efficiency reporting helper for building diverse dashboards.",
    featureCards: [
      {
        title: "Automated Reports",
        description: "Reports update automatically on schedule.",
      },
      {
        title: "Quick Data Cleanup",
        description: "Transform raw data into readable reports.",
      },
      {
        title: "Multiple Channels",
        description: "Supports Google Sheets and Data Studio.",
      },
    ],
    stepsTitle: "4 Steps to Connect SHOPLINE",
    steps: [
      {
        title: "1. Register an Account",
        description:
          "Create your account. QuickDash offers a permanent free trial. This connector requires the Premium plan.",
        image: {
          src: "/photos/login.png",
          alt: "Register account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. Bind Account",
        description: "Bind the SHOPLINE account you want to connect.",
        image: {
          src: "/photos/shopline.png",
          alt: "Bind account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. Paste Secret Key",
        description:
          "Paste the key to your target destination (Google Sheets / Data Studio).",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Paste secret key",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. Complete Import",
        description: "You are ready to import data and build reports.",
        image: {
          src: "/photos/SLReport.png",
          alt: "Complete import",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  oneshopPage: {
    metadataTitle: "1SHOP",
    heroTitle: "1SHOP",
    heroDescriptionLine1: "Automatically send data to third-party platforms.",
    heroDescriptionLine2: "Supports Google Sheets and Data Studio.",
    ctaLabel: "Available Metrics",
    ctaHref: "/articles/1shop-metrics/",
    supportedQueriesTitle: "Supported Queries",
    supportedQueries: ["Order Data"],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
    ],
    heroImage: {
      src: "/icon/one_shop.png",
      alt: "1SHOP",
      width: 320,
      height: 320,
      wrapperClassName:
        "rounded-2xl border border-white/40 flex items-center justify-center",
      imageClassName: "w-48 h-48 sm:w-64 sm:h-64 rounded-xl object-contain",
    },
    quickdashSectionTitle: "1SHOP Integration",
    quickdashSectionDescription:
      "A high-efficiency reporting helper for building diverse dashboards.",
    featureCards: [
      {
        title: "Automated Reports",
        description: "Reports update automatically on schedule.",
      },
      {
        title: "Quick Data Cleanup",
        description: "Transform raw data into readable reports.",
      },
      {
        title: "Multiple Channels",
        description: "Supports Google Sheets and Data Studio.",
      },
    ],
    stepsTitle: "5 Steps to Connect 1SHOP",
    steps: [
      {
        title: "1. Apply for API Key",
        description:
          "Join the official 1SHOP LINE to apply for an API key. Connecting the key requires the 1SHOP Unlimited plan.",
        image: {
          src: "/photos/add_1shop.png",
          alt: "Apply for API key",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. Register an Account",
        description:
          "Create your account. QuickDash offers a permanent free trial. This connector requires the Premium plan.",
        image: {
          src: "/photos/login.png",
          alt: "Register account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. Bind Account",
        description: "Paste the APP ID and APP SECRET obtained in step 1.",
        image: {
          src: "/photos/1shop_binding.png",
          alt: "Bind account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. Paste Secret Key",
        description:
          "Paste the key to your target destination (Google Sheets / Data Studio).",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Paste secret key",
          width: 760,
          height: 480,
        },
      },
      {
        title: "5. Complete Import",
        description: "You are ready to import data and build reports.",
        image: {
          src: "/photos/1shop_report.png",
          alt: "Complete import",
          width: 760,
          height: 480,
        },
      },
    ],
  },
  rutenPage: {
    metadataTitle: "RUTEN MARKET",
    heroTitle: "RUTEN MARKET",
    heroDescriptionLine1: "Automatically send data to third-party platforms.",
    heroDescriptionLine2: "Supports Google Sheets and Data Studio.",
    ctaLabel: "Available Metrics",
    ctaHref: "/articles/ruten-metrics/",
    supportedQueriesTitle: "Supported Queries",
    supportedQueries: ["Order Data"],
    supportedQueriesGridClass: "sm:grid-cols-2 lg:grid-cols-4",
    supportedDestinations: [
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0-1.gif",
        alt: "Google Sheets",
      },
      {
        src: "/wp-content/uploads/2025/09/%E5%BF%AB%E5%AE%A2%E7%B6%B2%E7%AB%99%E9%80%A3%E7%B5%90%E5%99%A8%E6%96%B9%E5%BD%A2%E5%9C%96%E7%89%87-300-x-300-%E5%83%8F%E7%B4%A0.gif",
        alt: "Data Studio",
      },
    ],
    heroImage: {
      src: "/icon/ruten.png",
      alt: "RUTEN MARKET",
      width: 320,
      height: 320,
      wrapperClassName:
        "rounded-2xl border border-white/40 flex items-center justify-center",
      imageClassName: "w-48 h-48 sm:w-64 sm:h-64 rounded-xl object-contain",
    },
    quickdashSectionTitle: "RUTEN MARKET Integration",
    quickdashSectionDescription:
      "A high-efficiency reporting helper for building diverse dashboards.",
    featureCards: [
      {
        title: "Automated Reports",
        description: "Reports update automatically on schedule.",
      },
      {
        title: "Data Cleanup",
        description: "Transform raw data into readable reports.",
      },
      {
        title: "Multiple Channels",
        description: "Supports Google Sheets and Data Studio.",
      },
    ],
    stepsTitle: "5 Steps to Connect RUTEN MARKET",
    steps: [
      {
        title: "1. Apply for API Key",
        description:
          "Complete the Ruten Marketplace API application form (please contact us via private message to request the form)",
        image: {
          src: "/photos/add_ruten.png",
          alt: "Apply for API key",
          width: 760,
          height: 480,
        },
      },
      {
        title: "2. Register an Account",
        description:
          "Create your account. QuickDash offers a permanent free trial. This connector requires the Premium plan.",
        image: {
          src: "/photos/login.png",
          alt: "Register account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "3. Bind Account",
        description: "Paste the APP ID and APP SECRET obtained in step 1.",
        image: {
          src: "/photos/ruten_binding.png",
          alt: "Bind account",
          width: 760,
          height: 480,
        },
      },
      {
        title: "4. Paste Secret Key",
        description:
          "Paste the key to your target destination (Google Sheets / Data Studio).",
        image: {
          src: "/wp-content/uploads/2025/09/landing.png",
          alt: "Paste secret key",
          width: 760,
          height: 480,
        },
      },
      {
        title: "5. Complete Import",
        description: "You are ready to import data and build reports.",
        image: {
          src: "/photos/ruten-report.png",
          alt: "Complete import",
          width: 760,
          height: 480,
        },
      },
    ],
  },

  aboutPage: {
    metadataTitle: "About QuickDash",
    heroTitleLine1: "Marketing & Agency",
    heroTitleHighlight: "Data Integration",
    heroDescription:
      "Help marketers and agencies track ad performance, visualize reports faster, and focus on data-driven decisions.",
    stats: [
      { value: "2000+", label: "Connected Users" },
      { value: "14h+", label: "Hours Saved" },
      { value: "10+", label: "Supported Platforms" },
      { value: "200%", label: "Work Efficiency" },
    ],
    featuresTitle: "Why QuickDash",
    featuresSubtitle:
      "Save 14+ working hours per month for marketers and agencies.",
    productFeatures: [
      {
        icon: "globe",
        title: "Localized Metrics",
        description:
          "Complete localized metrics and documentation to help you get started without translation overhead.",
      },
      {
        icon: "zap",
        title: "Fast Integration",
        description: "Connect major marketing data sources in under 3 minutes.",
      },
      {
        icon: "barChart3",
        title: "Visual Reports",
        description:
          "Generate easy-to-read visual reports for faster insight consumption.",
      },
      {
        icon: "users",
        title: "Taiwan Team Support",
        description:
          "Backed by a Taiwan-based technical team for smooth communication.",
      },
    ],
    platformsTitle: "Supported Integrations",
    platformsSubtitle:
      "Supports mainstream marketing platforms across META, Google, and LINE.",
    platforms: [
      { name: "META Ads", iconSrc: "/icon/fb.png" },
      { name: "Instagram", iconSrc: "/icon/ig.png" },
      { name: "Threads", iconSrc: "/icon/threads.png" },
      { name: "META Page", iconSrc: "/icon/fb.png" },
      { name: "Google Analytics 4", iconSrc: "/icon/ga4.png" },
      { name: "Google Ads", iconSrc: "/icon/gads.png" },
      { name: "Google Search Console", iconSrc: "/icon/gsc.png" },
      { name: "LINE LAP", iconSrc: "/icon/line.png" },
      { name: "CYBERBIZ Store", iconSrc: "/icon/cyberbiz.png" },
      { name: "SHOPIFY Store", iconSrc: "/icon/shopify.png" },
      // { name: "SHOPLINE Store", iconSrc: "/icon/shopline.jpg" },
      { name: "1SHOP Store", iconSrc: "/icon/one_shop.png" },
      { name: "Ruten", iconSrc: "/icon/ruten.png" },
      { name: "Microsoft Ads", iconSrc: "/icon/bing.png" },
      { name: "Lihi Short URL", iconSrc: "/icon/lihi2.png" },
    ],
    ctoBadge: "Spyder - Atai",
    ctoTitle: "Built by a Specialized Team",
    ctoParagraphs: [
      "Led early integrations in Taiwan, including META Ads, social connectors, and Google Ads. The team has deep hands-on experience with GA4, Facebook Ads, and Google Ads, and has delivered practical integration across major local data platforms.",
    ],
    ctoSocialLinks: [
      {
        label: "Facebook",
        href: "https://www.facebook.com/theodorekoung/",
        iconSrc: "/icon/fb.png",
      },
      {
        label: "Instagram",
        href: "https://www.instagram.com/atai_coding_data",
        iconSrc: "/icon/ig.png",
      },
      {
        label: "Threads",
        href: "https://www.threads.com/@atai_coding_data",
        iconSrc: "/icon/threads.png",
      },
    ],
    awardsSection: {
      sectionLabel: "Awards & Recognition",
      items: {
        growth: {
          title: "Gold Product Expert",
          subtitle: "Google Analytics 4",
          description:
            "Google GA4 Product Expert. Over 100 community solutions provided.",
        },
        strategy: {
          title: "Certified Developer",
          subtitle: "Data Studio",
          description:
            "Google Data Studio Connector Developer dedicated to streamlining data integration for end-users.",
        },
      },
      highlight: {
        title: "Gold Product Expert",
        subtitle: "Google Analytics 4",
        description:
          "Data Studio connector developer and a global GA4 Gold Product Expert.",
      },
      ariaPrevious: "View previous award",
      ariaNext: "View next award",
      ariaSwitchTo: "Switch to",
    },
    testimonialsTitle: "Recommended by experts",
    testimonials: [
      {
        name: "小圭老師",
        company: "Founder, Guihua Marketing",
        avatar: "/people/TURTLE.jpg",
        role: "Agency",
      },
      {
        name: "Dennis",
        company: "Founder, Digital Slide Room",
        avatar: "/people/DENNIS.jpg",
        role: "Data Analyst",
      },
      {
        name: "Bibi",
        company: "Founder, Yudong Digital",
        avatar: "/people/BIBI.jpg",
        role: "Agency",
      },
      {
        name: "LEO",
        company: "Digital Planning Dept., Cathay Financial",
        avatar: "/people/LEO.jpg",
        role: "Brand Team",
      },
      {
        name: "貝克菜",
        company: "CEO, lihi Marketing Tools",
        avatar: "/people/BK.png",
        role: "Brand Team",
      },
      {
        name: "Hugo Chen",
        company: "Founder, Xianbai Consulting",
        avatar: "/people/HUGO.jpg",
        role: "Agency",
      },
      {
        name: "Daisy",
        company: "Brand Marketing Consultant",
        avatar: "/people/Daisy.jpg",
        role: "Agency",
      },
    ],
    hackerSectionCtaText: "Visit Spyder",
    hackerSectionCtaHref: "https://spyder.com.tw/",
    hackerSectionTitle: "Digital Marketing Data Solutions Team",
    hackerSectionParagraphs: [
      "Spyder is a specialist team focused on digital marketing data solutions, including GA4 implementation, GTM event tracking, BigQuery analytics, and SEO conversion reporting.",
      "With 100+ GA4 and GTM projects delivered, the team helps Taiwan businesses build reliable tracking architecture and improve decision efficiency through precise integration and visual reporting.",
    ],
    hackerSectionServices: [
      "GA4 Analytics",
      "GTM Event Tracking",
      "BigQuery Analytics",
      "SEO Reporting",
    ],
    hackerSectionStats: [
      { value: "100+", label: "GA4 Projects" },
      { value: "100+", label: "GTM Projects" },
    ],
    hackerSectionAwardText: "Enterprise Data Solution Specialist",
  },
  contactPage: {
    metadataTitle: "Contact Us",
    heroTitle: "Contact Us",
    heroDescription:
      "If you have any QuickDash questions, please reach out via our official LINE account. Our team will assist you.",
    lineButtonLabel: "Join Official LINE",
    lineOfficialUrl:
      "https://line.me/R/ti/p/@070xojro?ts=12171155&oat_content=url",
  },
  refundPage: {
    metadataTitle: "Refund Policy",
    heroTitle: "Refund Policy",
    heroDescription:
      "Submit your refund request using the process below. Our support team will help calculate and confirm your refund amount.",
    lineButtonLabel: "Join Official LINE",
    lineOfficialUrl:
      "https://line.me/R/ti/p/@070xojro?ts=12171155&oat_content=url",
    stepsTitle: "Refund Process",
    steps: [
      {
        title: "Join the Official LINE Account",
        description:
          "Please add QuickDash official LINE first. Refund request and confirmation will be handled through LINE.",
      },
      {
        title: "Send the Following Messages",
        description:
          "Send the required messages in order so support can calculate your refund.",
        items: [
          'Send "I want a refund".',
          'Provide your "registered email".',
          "Wait for refund amount calculation and confirmation.",
        ],
      },
    ],
    notesTitle: "Refund Notes",
    notes: [
      "Each month is calculated as 30 days.",
      "A 5% handling fee applies to all refunds.",
      "All QuickDash services are terminated immediately after refund.",
      "Refund request date is based on when your LINE message is sent.",
      "Refund formula: remaining days × average plan price, minus 5% handling fee.",
      "Data Studio report permissions remain, but all connectors become invalid.",
      "Google Sheets schedules stop immediately.",
      "QuickDash reserves the right of final interpretation.",
    ],
  },
  policyPage: {
    metadataTitle: "Privacy Policy",
    heroTitle: "Privacy Protection Policy",
    heroDescription:
      "QuickDash (" +
      '"this website") explains this privacy policy so you can use our services with confidence. Please read the details below carefully.',
    sections: [
      {
        title: "1. Scope of Privacy Policy",
        paragraphs: [
          "This privacy policy explains how this website handles personally identifiable information collected when you use our services. It does not apply to external linked websites or personnel not commissioned or managed by this website.",
        ],
      },
      {
        title: "2. Collection, Processing, and Use of Personal Data",
        paragraphs: [
          "When you visit this website or use specific functions, we may ask for necessary personal data according to service nature and use it within the stated purpose. Without your written consent, we will not use personal data for other purposes.",
          "When you use interactive features such as service mailbox or surveys, we may retain provided information including name, email, contact details, and usage time. During normal browsing, servers may automatically record IP address, access time, browser type, and clickstream behavior for internal service improvement only.",
          "To provide accurate services, we may analyze survey content statistically. Published statistical results or descriptions will not identify specific individuals.",
        ],
      },
      {
        title: "3. Data Protection",
        paragraphs: [
          "Only authorized personnel may access your personal data. If third parties are entrusted for service needs, we require strict confidentiality and perform necessary checks to ensure compliance.",
        ],
        showRightsRequests: true,
      },

      {
        title: "4. Google User Data — Access, Use, and Protection",
        paragraphs: [""],
        subsections: [
          {
            heading: "Data Accessed",
            paragraphs: [
              "After user authorization through the Google consent flow, QuickDash will access the following data:",
            ],
            items: [
              "Metadata of related Google accounts and properties (GA4, GSC, GAds, etc.) (Account ID, Account Name, Property ID, Property Name).",
              "Aggregated report data based on user-selected metrics (e.g., sessions, total users, page views, conversions, revenue) and dimensions (e.g., source/medium, device category, country/region, landing page).",
            ],
            notes: [
              "QuickDash will not access Personally Identifiable Information (PII) such as individual user IDs, IP addresses, or email addresses through this authorization scope.",
            ],
          },
          {
            heading: "How Data is Used",
            paragraphs: [
              "Google user data is used solely for the following purposes:",
            ],
            items: [
              "To display the user's GA4 accounts and properties in the QuickDash backend.",
              "To retrieve and present GA4 report data based on the dimensions and metrics selected by the user.",
              "To allow users to actively export GA4 data to their chosen destinations, such as Google Sheets, Data Studio, or MCP-compatible analytics platforms.",
            ],
            notes: [
              "All data retrieval and export operations require explicit initiation by the user. Without direct user action, QuickDash will not automatically access, query, or process Google user data. Google user data will not be used for targeted advertising, credit or loan evaluation, or any purpose unrelated to the aforementioned analytics features.",
            ],
          },
          {
            heading: "Data Sharing and Transfer",
            paragraphs: [
              "QuickDash will not proactively sell, trade, or transfer Google user data to data brokers, ad networks, or any third parties.",
              "Data transfer only occurs when explicitly initiated by the user through QuickDash's export functions (Google Sheets, Data Studio, MCP). It is up to the user to decide when, where, and what data to export. QuickDash acts solely as an authorized data bridge for the user and will not determine the export destination on behalf of the user.",
            ],
          },
          {
            heading: "Data Protection",
            items: [
              "All data in transit is encrypted via HTTPS (TLS 1.2+).",
              "User authorization data is stored in encrypted volumes within the cloud infrastructure.",
              "Access to user data follows the 'principle of least privilege' and is restricted to authorized systems and personnel.",
              "Access logs are retained for auditing and tracking purposes.",
              "Regular security reviews and vulnerability patching are conducted.",
            ],
          },
          {
            heading: "Data Retention and Deletion",
            paragraphs: [
              "Tokens are retained only while the user's QuickDash account is active and the Google authorization remains connected.",
            ],
            items: [
              "Users can revoke Google authorization from their QuickDash account settings at any time. Once revoked, all stored Google tokens will be immediately deleted.",
              "Users can request account deletion via QuickDash LINE.",
              "Users can also revoke QuickDash's access permissions through their Google Account permissions page.",
            ],
          },
          {
            heading: "AI/Machine Learning Model Training",
            paragraphs: [
              "QuickDash will not use Google user data to develop, train, or improve any Artificial Intelligence (AI) or machine learning models (whether owned by QuickDash or any third party).",
            ],
          },
          {
            heading: "Compliance with Limited Use Policy",
            paragraphs: [
              "QuickDash's use and transfer to any other app of information received from Google APIs will strictly adhere to the Google API Services User Data Policy, including the Limited Use requirements.",
            ],
          },
        ],
      },

      {
        title: "5. External Links",
        paragraphs: [
          "This website may contain links to other websites. Those websites are not covered by this privacy policy; please refer to their own privacy policies.",
        ],
      },
      {
        title: "6. Policy on Sharing Personal Data with Third Parties",
        paragraphs: [
          "We do not provide, exchange, rent, or sell your personal data to individuals, groups, private companies, or public agencies, except where required by law or contractual obligation.",
        ],
        listIntro: "Exceptions include but are not limited to:",
        showThirdPartyExceptions: true,
      },
      {
        title: "7. Use of Cookies",
        paragraphs: [
          "To provide optimal service, this website may place and read cookies on your device. If you do not wish to accept cookies, set your browser privacy level to high. Some functions may not operate normally.",
        ],
      },
      {
        title: "8. Revisions to Privacy Policy",
        paragraphs: [
          "This privacy policy may be revised at any time when needed. Updated terms will be published on this website.",
        ],
      },
      {
        title: "9. Access and Deletion of Data",
        paragraphs: [
          "Members can review authorized name and email data in their account, used only for membership and order-related purposes. Upon request, personal data can be deleted or usage stopped through our contact channel.",
        ],
      },
    ],
    rightsRequestsTitle:
      "You may also request the following through Contact Us",
    rightsRequests: [
      "Right to delete personal data",
      "Right to erase personal information",
      "Request us to delete or remove your personal data",
      "Request deletion of your personal information",
    ],
    sensitiveProtectionTitle: "Protection Mechanisms for Sensitive Data",
    sensitiveProtection: [
      "Encrypted transmission and storage: all user data is transmitted via HTTPS (SSL/TLS), and sensitive stored data is encrypted to prevent unauthorized access.",
      "Access control: only authorized staff or contractors can access sensitive data under strict account and permission control.",
      "Access logs and audit: read/write actions on sensitive data are logged for future auditing and traceability.",
      "Regular risk assessment and patching: we regularly review system security, patch known vulnerabilities, and update based on best practices.",
      "Data minimization: we collect only the minimum data required and delete it when purpose is fulfilled.",
    ],
    thirdPartyExceptions: [
      "With your written consent.",
      "Where explicitly required by law.",
      "To prevent danger to your life, body, freedom, or property.",
      "Cooperation with public agencies or academic institutions for public-interest statistics/research where data is processed to avoid identifying individuals.",
      "When your behavior violates terms or harms rights of the website/users, and disclosure is necessary to identify, contact, or take legal action.",
      "When beneficial to your interests.",
      "When this website entrusts vendors to collect/process/use personal data and exercises due supervision responsibility.",
    ],
    contactEmailLabel: "For related requests, email:",
    contactEmail: "spyder@spyder.com.tw",
    contactLinkPrefix: "Or visit",
    contactLinkLabel: "Contact Us",
    contactLinkSuffix: "to submit your request.",
  },
  termPage: {
    metadataTitle: "Terms of Service",
    heroTitle: "Terms of Service",
    heroDescription:
      "This page contains the full Terms of Service for QuickDash. By accessing or using this service, you agree to these terms.",
    introParagraphs: [
      "These Terms of Service (" +
        '"Terms") govern your use of QuickDash products and related services provided by Spyder Co., Ltd. ("Company", "we", or "platform").',
      "These Terms form a legally binding agreement between the Company and all users (registered members and visitors). By accessing, browsing, or using this service, you acknowledge that you have read, understood, and agreed to all terms and applicable laws. If you disagree with any part, please stop using the service immediately.",
      "We reserve the right to modify these Terms and service content at any time to reflect service or legal updates. Changes will be published on the service page. Continued use after updates constitutes acceptance of revised terms.",
      "The Company may modify these Terms with notice on the service page. Except as otherwise stated, the Company is not required to bear specific liability to users or third parties for exercising rights to modify or discontinue services.",
      "If any part of these Terms is deemed invalid by a competent court, the remaining provisions remain effective. Before amendment, relevant matters are governed by Taiwan Civil Code, Consumer Protection Act, and other applicable laws.",
      "When member users browse or use QuickDash services, they are deemed to have reviewed these terms in detail and agreed to all rules herein.",
    ],
    sections: [
      {
        title: "Service Overview",
        paragraphs: [
          "QuickDash primarily provides data integration services. Users may connect data via free registration or paid plans.",
          "The Company may add, change, or remove systems/features without prior notice to free users. If users paid for removed features, refunds will be handled according to law. Significant changes affecting paid member rights will be notified within two months.",
          "You agree that we may use your registered email or other contact information to send service announcements and promotional messages.",
        ],
      },
      {
        title: "Registration and Account Management",
        paragraphs: [
          "After completing registration, users obtain member status and can use full member functions.",
          "You warrant that submitted personal information is accurate, complete, and current. If data changes, please update promptly. We may suspend/terminate accounts if data is false, incomplete, or outdated.",
          "You are responsible for safeguarding your account and password, and all activities under your credentials. Do not lend or transfer your account. Losses caused by poor credential management are your responsibility.",
          "If you suspect your account/password is compromised, notify support immediately so we can respond.",
          "Your member data is used for service provision. Unless required by law or authority order, we will not intentionally disclose your data to unrelated third parties.",
          "You may review/correct your member data and request account deletion. After deletion, dependent services may become unavailable.",
          "You agree to be fully responsible for all activities under your account in Company services.",
        ],
      },
      {
        title: "Data Processing",
        paragraphs: [
          "The Company may optimize or upgrade product functions at any time based on professional judgment and market needs.",
          "To provide better support (e.g., troubleshooting, configuration, operational support), if access to your related data is needed, our staff will request your consent first. Without consent, we have no authority to access, disclose, or store such data.",
          "The Company does not store any data generated from your use of this product. Account data is only used in terminal display interfaces.",
        ],
      },
      {
        title: "Membership Termination and Refund Rules",
        paragraphs: [
          "If you wish to terminate service during your subscription period and request a refund, contact us through official LINE. Notes are as follows:",
        ],
        list: [
          "Each plan month is calculated as 30 days. Example: 5 months and 10 days remaining equals 160 days (30×5 + 10).",
          "A 5% handling fee applies to refunds.",
          "After refund, all QuickDash services become unavailable.",
          "If Data Studio is connected, report viewing may remain but all connectors become invalid.",
          "Paid members who redeemed any copyable report products provided by QuickDash are not eligible for refunds.",
          "QuickDash reserves final interpretation rights.",
        ],
      },
      {
        title: "Member Account Usage Rules",
        paragraphs: [
          "Registered member accounts can be used to exercise membership rights. Completing registration and using the service means acceptance of these terms.",
          "When registering, provide accurate personal information on the member center registration page. To resolve account issues, identity verification documents may be required.",
          "Each valid identity document can register one member account. Successful registration means account creation is complete.",
          "If false/outdated information is found during verification, services may be suspended until accurate data is provided.",
          "To protect member rights, account IDs cannot be modified.",
          "If account theft is suspected, the Company may terminate account usage immediately. Access can be restored after verification.",
          "Members are responsible for keeping account/password confidential. All liabilities caused by poor credential management, transfer, or lending are borne by members.",
        ],
        list: [
          "Avoid weak/easy-to-guess passwords or using account ID as password.",
          "Do not share account or password with others.",
          "If anyone requests account, password, or ID number in the name of an event, do not provide it. Contact QuickDash support for verification.",
        ],
      },
      {
        title: "Intellectual Property Notice",
        paragraphs: [
          "All works, data, and page arrangement on QuickDash, including copyrights, patents, trademarks, trade secrets, and related IP rights, belong to QuickDash owner Spyder Co., Ltd. or authorized rights holders. Without prior authorization, reproduction, adaptation, editing, distribution, transmission, broadcasting, rental, sale, or other use is prohibited.",
          "Website content and programs are QuickDash intellectual property. Unauthorized reproduction, reverse engineering, de-compiling, or disassembling functions/programs is prohibited.",
          "Users must respect and legally use the website. Otherwise, they are responsible for consequences per these terms.",
          "QuickDash is a data-processing platform. Upon receiving valid infringement notice, we may remove related account/data without prior notice.",
          "Users conducting illegal activities via this platform bear full legal responsibility. The Company may pursue legal action when necessary.",
        ],
      },
      {
        title: "User Legal Obligations and Commitments",
        paragraphs: [
          "You agree not to use QuickDash for unlawful purposes or by unlawful means, and to comply with Taiwan laws and international internet conventions. Users outside Taiwan must also comply with local laws. You must not infringe rights of third parties, QuickDash, Spyder personnel, affiliates, advertisers, partners, or distributors.",
          "If users violate laws or infringe website rights, QuickDash and Spyder may suspend or terminate service access without prior notice.",
          "Interpretation and disputes related to these terms are governed by Taiwan law, with Kaohsiung District Court as court of first instance.",
          "Where legally liable, the Company's compensation cap is limited to annual revenue actually received from the specific user for this service.",
        ],
      },
      {
        title: "Consent for Personal Data Provision",
        paragraphs: [
          "Spyder Co., Ltd. processes personal data in accordance with the Personal Data Protection Act, related laws, and its privacy policy to provide comprehensive services while protecting user rights and privacy.",
          "Collection purposes follow legally defined categories, including marketing, technology management, customer management, training administration, commercial/technical information, statistics/analysis, database management, membership management, and other business-related needs.",
          "Depending on chosen services, you may need to provide one or more categories of personal data as follows:",
          "Spyder may use your data during operation period. Data usage area is global.",
          "Your data may be used for membership verification/management, payment, logistics, marketing events, reward redemption, analytics, personalization (e.g., newsletters, SMS, online messages), important notices/updates, and other lawful business purposes. For service fulfillment, data may be shared within necessary scope with affiliates, merchants serving you, and cooperating vendors.",
          "For optional fields, you may choose whether to provide data. However, certain features/services may become unavailable without required data. If identity cannot be verified due to missing or false data, your request may not be processed.",
          "Per applicable law, requests for access, copy, correction, suspension of processing/use, deletion, or opting out of specific/general marketing can be submitted via company email.",
          "The Company reserves the right to revise this notice. Revisions will be published on the website and may also be notified via your provided email or other contact information.",
          "QuickDash reserves final interpretation and revision rights for this agreement.",
        ],
        list: [
          "C001 Identifiers (e.g., name, phone, address, email).",
          "C023 Family member details (e.g., family status, legal guardian name).",
          "C035 Leisure activities and interests (e.g., hobbies).",
        ],
      },
    ],
    contactEmailLabel: "For related requests, email:",
    contactEmail: "spyder@spyder.com.tw",
    linksPrefix: "You can also visit",
    refundLinkLabel: "Refund Policy",
    linksAnd: "and",
    policyLinkLabel: "Privacy Policy",
    linksSuffix: "for more details.",
  },
  articlesPage: {
    metadataTitle: "Articles",
    metadataDescription:
      "The latest articles on connector metrics, data integration, and practical reporting workflows.",
    heroTitle: "Articles",
    heroDescription:
      "The latest articles on connector metrics, data integration, and practical reporting workflows.",
    detailDescriptionFallback:
      "Learn practical connector metrics and data integration strategies from QuickDash.",
  },
  notFoundPage: {
    metadataTitle: "Page Not Found",
    code: "404",
    heading: "We couldn't find that page",
    description:
      "This link may have moved, been renamed, or contains a typo. Here are the pages people look for most — pick one and keep going.",
    links: [
      {
        label: "Back to Home",
        description: "See what QuickDash can do for your team",
        href: "/",
      },
      {
        label: "Plans & Pricing",
        description: "Find the plan that fits your team",
        href: "/plans",
      },
      {
        label: "Setup Guides",
        description: "Connect your data sources step by step",
        href: "/tutorial",
      },
      {
        label: "Articles",
        description: "Hands-on notes on metrics and reporting",
        href: "/articles",
      },
    ],
    contactPrefix: "Still can't find what you need?",
    contactLabel: "Contact Us",
  },
  common: {
    backToArticles: "← Back to Articles",
    articleNotFound: "Article Not Found",
    noExcerpt: "No excerpt available",
    noCover: "No cover image",
    untitledArticle: "(Untitled Article)",
    authorPrefix: "Author: ",
    datePrefix: "Published: ",
    tryQuickDash: "Try QuickDash Now",
  },
};

export default en;
